/**
 * Grade-tier mapping.
 *
 * The codebase historically only knew "kids vs not kids" (via KIDS_GRADE_LEVELS).
 * That binary is too coarse: pre-K and Primary 5 are both "kids" but they should
 * not see the same UI. A 5-year-old needs huge buttons, very short sessions,
 * and zero text-heavy question types. A 10-year-old in P5 can handle longer
 * sessions, more types, and less mascot density.
 *
 * Tiers (most → least scaffolding):
 *   - early_kids   pre_K, kindergarten, lower_primary, P1
 *   - upper_kids   P2 – P5
 *   - upper_primary P6 (independent interface, primary practice)
 *   - jhs          middle_school
 *   - shs          high_school
 *   - tertiary     college
 *
 * `isKidsMode` (existing binary) = tier === 'early_kids' || tier === 'upper_kids'.
 * Callers that need finer control should switch on the tier itself.
 */

// Note: `gradeLevel` arrives as a free-text string from the user profile
// (the GradeLevel union is a client-side concern). We accept any string and
// only depend on the canonical values that EARLY_KIDS_GRADES etc. enumerate.

export type GradeTier = "early_kids" | "upper_kids" | "upper_primary" | "jhs" | "shs" | "tertiary";

export interface GradeTierProfile {
    tier: GradeTier;
    isKids: boolean;
    /** Hard cap on questions per session. */
    maxQuestions: number;
    /** Soft default when the user hasn't picked a count. */
    defaultQuestions: number;
    /** Question types this tier is allowed to see in the creator. */
    allowedQuestionTypes: string[];
    /** Difficulty levels this tier should see. */
    allowedDifficulties: ("beginner" | "intermediate" | "advanced" | "expert")[];
    /** Can this tier do timed exam mode at all. */
    allowsExamMode: boolean;
    /** Can this tier write essay / long-answer responses. */
    allowsEssay: boolean;
    /** UI density hint for renderers that want larger touch targets / fewer choices. */
    uiDensity: "playful" | "friendly" | "standard" | "dense";
}

const EARLY_KIDS_GRADES: string[] = ["pre_k", "kindergarten", "lower_primary", "primary_1"];
const UPPER_KIDS_GRADES: string[] = ["primary_2", "primary_3", "primary_4", "primary_5"];
const JHS_GRADES: string[] = ["middle_school", "junior_high", "lower_secondary", "jhs", "jhs_1", "jhs_2", "jhs_3", "13_15"];
const SHS_GRADES: string[] = ["high_school", "upper_secondary", "shs", "shs_1", "shs_2", "shs_3", "16_18"];
const TERTIARY_GRADES: string[] = ["college", "tertiary", "university", "adult"];
const YEAR_QUESTION_COUNTS: Record<string, number> = {
    primary_2: 8, primary_3: 8, primary_4: 8, primary_5: 10, primary_6: 12,
    jhs_1: 15, jhs_2: 15, jhs_3: 20, shs_1: 15, shs_2: 20, shs_3: 30,
};

const TIER_PROFILES: Record<GradeTier, Omit<GradeTierProfile, "tier" | "isKids">> = {
    early_kids: {
        maxQuestions: 10,
        defaultQuestions: 8,
        allowedQuestionTypes: ["mcq", "true_false"],
        allowedDifficulties: ["beginner"],
        allowsExamMode: false,
        allowsEssay: false,
        uiDensity: "playful",
    },
    upper_kids: {
        maxQuestions: 20,
        defaultQuestions: 10,
        allowedQuestionTypes: ["mcq", "true_false", "matching"],
        allowedDifficulties: ["beginner"],
        allowsExamMode: false,
        allowsEssay: false,
        uiDensity: "friendly",
    },
    upper_primary: {
        maxQuestions: 20,
        defaultQuestions: 10,
        allowedQuestionTypes: ["mcq", "true_false", "matching", "fill_in_blank", "subjective_short"],
        allowedDifficulties: ["beginner", "intermediate"],
        allowsExamMode: false,
        allowsEssay: false,
        uiDensity: "standard",
    },
    jhs: {
        maxQuestions: 100,
        defaultQuestions: 20,
        allowedQuestionTypes: [
            "mcq",
            "true_false",
            "matching",
            "fill_in_blank",
            "subjective_short",
            "subjective_long",
        ],
        allowedDifficulties: ["beginner", "intermediate"],
        allowsExamMode: true,
        // BECE Paper 2 contains essay / structured-response work; JHS users
        // need essay practice ahead of those papers.
        allowsEssay: true,
        uiDensity: "standard",
    },
    shs: {
        maxQuestions: 100,
        defaultQuestions: 30,
        allowedQuestionTypes: [
            "mcq",
            "true_false",
            "matching",
            "ordering",
            "fill_in_blank",
            "subjective_short",
            "subjective_long",
        ],
        allowedDifficulties: ["beginner", "intermediate", "advanced"],
        allowsExamMode: true,
        allowsEssay: true,
        uiDensity: "standard",
    },
    tertiary: {
        maxQuestions: 100,
        defaultQuestions: 40,
        allowedQuestionTypes: [
            "mcq",
            "true_false",
            "matching",
            "ordering",
            "fill_in_blank",
            "subjective_short",
            "subjective_long",
        ],
        allowedDifficulties: ["beginner", "intermediate", "advanced", "expert"],
        allowsExamMode: true,
        allowsEssay: true,
        uiDensity: "dense",
    },
};

export function getGradeTier(gradeLevel?: string | null): GradeTier {
    if (!gradeLevel) return "shs"; // sensible default for unknown — most users.
    const g = String(gradeLevel).trim().toLowerCase().replace(/[ -]+/g, "_");
    if (EARLY_KIDS_GRADES.includes(g)) return "early_kids";
    if (UPPER_KIDS_GRADES.includes(g)) return "upper_kids";
    if (g === "primary_6") return "upper_primary";
    if (JHS_GRADES.includes(g)) return "jhs";
    if (SHS_GRADES.includes(g)) return "shs";
    if (TERTIARY_GRADES.includes(g)) return "tertiary";
    return "shs";
}

export function getGradeTierProfile(gradeLevel?: string | null): GradeTierProfile {
    const tier = getGradeTier(gradeLevel);
    const profile = TIER_PROFILES[tier];
    return {
        tier,
        isKids: tier === "early_kids" || tier === "upper_kids",
        ...profile,
        defaultQuestions: YEAR_QUESTION_COUNTS[String(gradeLevel || "").trim().toLowerCase().replace(/[ -]+/g, "_")] ?? profile.defaultQuestions,
    };
}

/** Convenience: does this tier need the kid-friendly UI track? */
export function isKidsTier(tier: GradeTier): boolean {
    return tier === "early_kids" || tier === "upper_kids";
}

/** Onboarding stores the school year separately from the broad interface level. */
export function resolveGradeLevel(preferences?: {
    grade?: unknown;
    grade_level?: string | null;
    gradeLevel?: string | null;
    education_level?: string | null;
    preferences?: { grade?: unknown } | null;
} | null, fallback?: string | null): string {
    const detail = preferences?.preferences?.grade || preferences?.grade;
    const legacy = preferences?.grade_level || preferences?.gradeLevel || "";
    const hasSpecificGrade = /^(primary[ _-][1-6]|[js]hs[ _-][1-3]|pre_k|kindergarten|lower_primary)$/i.test(legacy.trim());
    const grade = typeof detail === "string" && /^(primary[ _-][1-6]|[js]hs[ _-][1-3]|pre_k|kindergarten)$/i.test(detail.trim())
        ? detail
        : hasSpecificGrade ? legacy : preferences?.education_level || legacy || fallback || "";
    return grade.trim().toLowerCase().replace(/[ -]+/g, "_");
}
